<template>
  <div
    v-if="images && images.length > 0"
    class="flex w-full"
  >
    <div
      v-if="images.length > 1"
      class="relative"
    >
      <div class="overflow-hidden mr-4">
        <ScrollSnapSlider
          ref="slider"
          :items="images"
          slider-class="h-[540px] w-[120px] main-swiper-desktop flex flex-col gap-y-5"
          :get-item-key="(image) => image.medium"
          @slide-change="handleSlideChange"
        >
          <template #default="{ item: image, index }">
            <div class="relative rounded-[20px] overflow-hidden">
              <NuxtImg
                :src="image.medium"
                :class="{ 'opacity-30': isUnavailable }"
                :title="`${lotName} напрокат — Фото ${index + 1}`"
                :alt="`Возьмите ${lotName} напрокат (Фото ${index + 1}) ${$t(
                  `common.inCity.${lotCity.slug}`
                )}`"
                class="w-[120px] h-[120px] overflow-hidden cursor-pointer flex justify-center items-center relative object-cover"
                loading="lazy"
                fetchpriority="high"
                height="120"
                width="120"
                @click="handleClickThumbImage(image, index)"
              />
              <AdultWrapper v-if="needBlur" />
            </div>
          </template>
        </ScrollSnapSlider>
      </div>
      <div
        v-if="images.length > 1"
        class="absolute bottom-0 left-0 z-10 bg-gradient-to-b from-transparent to-gray-100 w-[120px] h-10 pointer-events-none"
      />
    </div>
    <div
      class="rounded-2xl overflow-hidden flex justify-center items-center w-full bg-gray-300 relative xl:min-w-[773px]"
      style="height: 540px; width: 100%;"
    >
      <NuxtImg
        v-if="mainImage && mainImage.src"
        :key="mainImage.src"
        :src="mainImage.src"
        :class="{ 'opacity-30': isUnavailable }"
        :title="`${lotName} напрокат — Фото ${currentSlide + 1}`"
        :alt="`Возьмите ${lotName} напрокат (Фото ${currentSlide + 1}) ${$t(
          `common.inCity.${lotCity.slug}`
        )}`"
        class="overflow-hidden object-contain max-h-full max-w-full cursor-pointer h-full"
        loading="eager"
        fetchpriority="high"
        densities="x1"
        @click="handleClickMainImage(mainImage)"
      />
      <AdultWrapper v-if="needBlur" />
      <div class="top-5 left-5 absolute z-20">
        <slot name="top-left" />
      </div>
      <div class="top-5 right-5 absolute">
        <slot name="actions" />
      </div>
    </div>
    <ClientOnly>
      <component
        :is="VueEasyLightbox"
        v-if="images.length > 0 && isClient && VueEasyLightbox"
        :visible="visibleRef"
        :imgs="lightboxImages"
        :index="indexRef"
        @hide="onHide"
      />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, shallowRef, watch } from 'vue'
import AdultWrapper from '~/components/lots/display_type/adult_wrapper.vue'
import ScrollSnapSlider from '~/components/shared/scroll_snap_slider.vue'
import { useLot } from '~/composables/useLot'

const props = defineProps({
  lotId: {
    type: [String, Number],
    required: true,
  },
  needBlur: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Array,
    default: () => [],
  },
})

const { lot, isUnavailable } = useLot(props.lotId)
const lotName = computed(() => lot.value.name)
const lotCity = computed(() => lot.value.city)

const currentImageIndex = ref(0)
const slider = ref(null)
const visibleRef = ref(false)
const indexRef = ref(0)
const isClient = ref(false)
const VueEasyLightbox = shallowRef(null)

const mainImage = computed(() => {
  if (!props.images || props.images.length === 0) {
    return null
  }
  return props.images[currentImageIndex.value] || props.images[0]
})

const currentSlide = computed(() => {
  return currentImageIndex.value
})

const lightboxImages = computed(() => {
  return props.images.map(image => ({
    src: image.src,
    title: `${lotName.value} напрокат`,
  }))
})

watch(
  () => props.images,
  newImages => {
    if (newImages && newImages.length > 0) {
      currentImageIndex.value = 0
    }
  },
  { immediate: true },
)

onMounted(async () => {
  if (import.meta.client) {
    isClient.value = true
  }
})

const handleSlideChange = (index) => {
  currentImageIndex.value = index
}

const handleClickThumbImage = (image, index) => {
  currentImageIndex.value = index
  if (slider.value) {
    slider.value.slideTo(index)
  }
}

const loadVueEasyLightbox = async () => {
  if (!VueEasyLightbox.value && import.meta.client) {
    const { default: VueEasyLightboxComponent } = await import('vue-easy-lightbox')
    VueEasyLightbox.value = VueEasyLightboxComponent
  }
}

const handleClickMainImage = async image => {
  await loadVueEasyLightbox()
  const index = props.images.findIndex(img => img.id === image.id)
  indexRef.value = index >= 0 ? index : 0
  visibleRef.value = true
}

const onHide = () => {
  visibleRef.value = false
}
</script>

<style lang="sass" scoped>
  :deep(.scroll-snap-slider)
    scroll-snap-type: y mandatory

  :deep(.scroll-snap-slide)
    scroll-snap-align: start
</style>
